import { useMemo, useState } from "react";
import { useWebClientApi } from "src/api";
import {
  fromBorrowerPersonalInfo,
  toCitizenshipUpdateReq,
  type PersonalInfoFormData,
} from "src/api/adapters";
import { useResponseRedirect } from "src/hooks";
import { useDlaFormSubmit } from "src/hooks/useDlaFormSubmit";
import { useFormLoad } from "src/hooks/useFormLoad";
import FormLayout from "src/layouts/FormLayout";
import { Col } from "src/components/layout";
import { InfoIconButton } from "src/components/InfoIconButton";
import CitizenshipModal from "./CitizenshipModal";
import {
  CitizenshipFields,
  FormTable,
  FormTableBody,
  UserAccountIcon,
} from "@porchpass/ui";
import type { DtoBorrower } from "@porchpass/api-client";
import {
  fromCitizenshipFormData,
  toCitizenshipFormData,
} from "src/forms/adapters/citizenship";

type FormattedTitleProps = {
  iconClickHandler: () => void;
};

const FormattedTitle = ({ iconClickHandler }: FormattedTitleProps) => (
  <>
    <span data-replay-safe={true}>Perfect, let&#39;s start!</span>
    <br />
    <span data-replay-safe={true}>
      Are you a&nbsp;<strong>U.S. Citizen?</strong>
      <InfoIconButton
        marginLeft="8px"
        onClick={iconClickHandler}
        data-replay-safe={true}
        tabIndex={0}
      />
    </span>
  </>
);

export default () => {
  const api = useWebClientApi();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { formData } = useFormLoad<PersonalInfoFormData>(
    {},
    () => api.getBorrower(),
    (data) => fromBorrowerPersonalInfo(data as DtoBorrower),
  );
  const [submitHandler, loading, response] = useDlaFormSubmit(
    (data: PersonalInfoFormData) =>
      api.updateBorrower(toCitizenshipUpdateReq(data)),
  );
  useResponseRedirect("/personal-info/marital-status", response);

  const citizenshipValue = useMemo(
    () => toCitizenshipFormData(formData?.citizenship_type),
    [formData?.citizenship_type],
  );

  return (
    <FormLayout
      title={<FormattedTitle iconClickHandler={() => setIsModalOpen(true)} />}
      headerTitle="Personal and Household"
      stepIndex={1}
      isLoading={loading}
    >
      <Col>
        <FormTable
          title="Citizenship Status"
          headerIcon={
            <UserAccountIcon
              variant="bulk-rounded"
              size={16}
              className="text-[#6F41E6]"
            />
          }
        >
          <FormTableBody>
            <CitizenshipFields
              value={citizenshipValue}
              onChange={(patch) => {
                const next = { ...citizenshipValue, ...patch };
                const payload = fromCitizenshipFormData(next);
                if (payload) {
                  void submitHandler(payload);
                }
              }}
              onCitizenSelection={(isCitizen) => {
                if (isCitizen) {
                  void submitHandler({ citizenship_type: "CITIZEN" });
                }
              }}
            />
          </FormTableBody>
        </FormTable>
      </Col>
      <CitizenshipModal
        isOpen={isModalOpen}
        closeHandler={() => setIsModalOpen(false)}
      />
    </FormLayout>
  );
};
